import _config from '@/config';
import store from '@/store';
import service from '@/api-service';
import { Toast } from 'vant';

var wx = require('weixin-js-sdk') || window['wx'];

/**
 * 添加缓存破坏参数
 * @param {string} url - 原始URL
 * @param {boolean} isPCWeChat - 是否为PC端微信
 * @returns {string} 添加参数后的URL
 */
function addNoCacheParam(url, isPCWeChat) {
    const urlObj = new URL(url);
    urlObj.searchParams.set('v', _config.version);
    urlObj.searchParams.set('t', new Date().getTime());
    if (isPCWeChat) {
        // PC端添加额外的缓存破坏参数
        urlObj.searchParams.set('pc', '1');
        urlObj.searchParams.set('_', Math.random().toString(36).substr(2, 9));
        urlObj.searchParams.set('cache', Date.now());
        urlObj.searchParams.set('rand', Math.random().toString(36).substr(2, 15));
    }
    return urlObj.href;
}

/**
 * 显示调试信息
 * @param {string|object} msg - 要显示的消息
 */
export function showDebugInfo(msg) {
    return;
    console.log(msg); // 启用调试信息，帮助排查问题
    // 创建或获取日志容器
    let debugDiv = document.getElementById('wx-debug-info');
    if (!debugDiv) {
        debugDiv = document.createElement('div');
        debugDiv.id = 'wx-debug-info';
        debugDiv.style.cssText = `
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(0,0,0,0.8);
            color: white;
            font-size: 12px;
            padding: 10px;
            max-height: 200px;
            overflow-y: auto;
            z-index: 9999;
            font-family: monospace;
            border-top: 1px solid #333;
        `;
        document.body.appendChild(debugDiv);
    }
    
    // 添加时间戳
    const timestamp = new Date().toLocaleTimeString();
    const logItem = document.createElement('div');
    logItem.style.marginBottom = '5px';
    logItem.style.borderBottom = '1px solid #444';
    logItem.style.paddingBottom = '3px';
    
    const message = typeof msg === 'object' ? JSON.stringify(msg, null, 2) : msg;
    logItem.innerHTML = `<span style="color: #aaa;">[${timestamp}]</span> ${message}`;
    
    debugDiv.appendChild(logItem);
    debugDiv.scrollTop = debugDiv.scrollHeight;
    
    // 限制日志条数，避免内存泄漏
    if (debugDiv.children.length > 50) {
        debugDiv.removeChild(debugDiv.firstChild);
    }
}

/**
 * 检测微信环境
 * @returns {object} 环境检测结果
 */
function detectWeChatEnvironment() {
    const ua = navigator.userAgent.toLowerCase();
    const isWeChatBrowser = /micromessenger/i.test(ua);
    const isWeChatPC = /windows wechat/i.test(ua) || /wxwork/i.test(ua) || /wechat/i.test(ua);
    const isWeChatMac = /macwechat/i.test(ua);
    const isMobile = /mobile|android|iphone|ipad|ipod/i.test(ua);
    const isPCEnvironment = !isMobile && (navigator.platform.includes('Win') || navigator.platform.includes('Mac'));
    const isPCWeChat = (isWeChatPC || isWeChatMac || (isWeChatBrowser && isPCEnvironment));
    
    return {
        isWeChatBrowser,
        isWeChatPC,
        isWeChatMac,
        isMobile,
        isPCEnvironment,
        isPCWeChat
    };
}

/**
 * 清除缓存
 * @param {boolean} isPCWeChat - 是否为PC端微信
 */
function clearCache(isPCWeChat) {
    try {
        showDebugInfo('开始清除缓存...');
        
        // 清除WeixinJSBridge缓存
        if (window.WeixinJSBridge && window.WeixinJSBridge.invoke) {
            window.WeixinJSBridge.invoke('clearStorage');
            showDebugInfo('WeixinJSBridge缓存清除成功');
        }
        
        // PC端微信额外缓存清理
        if (isPCWeChat) {
            showDebugInfo('PC端微信执行额外缓存清理...');
            
            // 清除localStorage
            try {
                localStorage.clear();
                showDebugInfo('localStorage清理完成');
            } catch (e) {
                showDebugInfo(`localStorage清理失败: ${e.message}`);
            }
            
            // 清除sessionStorage
            try {
                sessionStorage.clear();
                showDebugInfo('sessionStorage清理完成');
            } catch (e) {
                showDebugInfo(`sessionStorage清理失败: ${e.message}`);
            }
            
            // 清除ServiceWorker
            if (navigator.serviceWorker) {
                navigator.serviceWorker.getRegistrations().then(function(registrations) {
                    for(let registration of registrations) {
                        registration.unregister();
                    }
                    showDebugInfo('ServiceWorker清除完成');
                });
            }
        }
    } catch (e) {
        showDebugInfo(`清除缓存失败: ${e.message}`);
    }
}

// 防止重复执行授权流程的标记
let isAuthInProgress = false;

// 页面加载状态检查
let pageLoadTime = Date.now();

// 防止重复弹框的标记
let toastShown = false;

// 授权流程执行次数限制
let authAttemptCount = 0;
const MAX_AUTH_ATTEMPTS = 3;

// 页面刷新时重置状态
window.addEventListener('beforeunload', () => {
    isAuthInProgress = false;
    toastShown = false;
    authAttemptCount = 0;
    showDebugInfo('页面即将刷新，重置授权状态');
});

// 页面加载完成后重置部分状态
window.addEventListener('load', () => {
    // 重置弹框标记，但保留尝试次数限制
    toastShown = false;
    showDebugInfo('页面加载完成，重置弹框状态');
});

export function wxLogin(redirectUrl) {
    return new Promise((resolve, reject) => {
        // 防止重复执行
        if (isAuthInProgress) {
            showDebugInfo('授权流程已在执行中，请勿重复操作');
            reject(new Error('授权流程已在执行中'));
            return;
        }
        
        // 检查授权尝试次数
        if (authAttemptCount >= MAX_AUTH_ATTEMPTS) {
            showDebugInfo(`授权尝试次数已达上限(${MAX_AUTH_ATTEMPTS})，请刷新页面重试`);
            Toast.fail(`授权尝试次数过多，请刷新页面重试`);
            reject(new Error('授权尝试次数过多'));
            return;
        }
        
        // 检查页面是否刚加载（防止页面刷新时的重复执行）
        const currentTime = Date.now();
        if (currentTime - pageLoadTime < 3000) {
            showDebugInfo('页面刚加载，等待稳定后再执行授权');
            setTimeout(() => {
                wxLogin(redirectUrl).then(resolve).catch(reject);
            }, 3000);
            return;
        }
        
        isAuthInProgress = true;
        authAttemptCount++;
        showDebugInfo(`开始微信登录流程... (第${authAttemptCount}次尝试)`);

        // 输出当前环境信息（减少重复信息）
        if (!window.envInfoShown) {
            showDebugInfo('当前UA: ' + navigator.userAgent);
            showDebugInfo('当前URL: ' + window.location.href);
            showDebugInfo('传入的redirectUrl: ' + redirectUrl);
            window.envInfoShown = true;
        }
        
        // 检测微信环境
        const env = detectWeChatEnvironment();
        showDebugInfo(`环境检测结果: ${JSON.stringify(env)}`);
        
        // 如果不在微信环境中，给出提示
        if (!env.isWeChatBrowser && !env.isWeChatPC && !env.isWeChatMac) {
            showDebugInfo('当前不在微信环境中，请在微信中打开');
            Toast.fail('请在微信中打开此页面');
            reject(new Error('非微信环境'));
            return;
        }
        
        // PC端微信特殊处理 - 更宽松的检测条件
        if (env.isPCWeChat) {
            showDebugInfo('检测到PC端微信环境，使用特殊处理逻辑');
            showDebugInfo('PC端微信版本信息：');
            showDebugInfo(`- User Agent: ${navigator.userAgent}`);
            showDebugInfo(`- Platform: ${navigator.platform}`);
            showDebugInfo(`- 是否为Windows: ${navigator.platform.includes('Win')}`);
            showDebugInfo(`- 是否为Mac: ${navigator.platform.includes('Mac')}`);
            showDebugInfo(`- 微信PC端标识: ${/windows wechat/i.test(navigator.userAgent) || /wxwork/i.test(navigator.userAgent) || /wechat/i.test(navigator.userAgent) || /macwechat/i.test(navigator.userAgent)}`);
        }
        
        // 检查HTTPS协议
        if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
            showDebugInfo('微信授权需要HTTPS协议');
            Toast.fail('请使用HTTPS协议访问');
            reject(new Error('非HTTPS协议'));
            return;
        }
        
        // 清除缓存
        clearCache(env.isPCWeChat);
        
        /**
         * 处理有code参数的情况
         * @param {string} code - 授权码
         * @param {boolean} isPCWeChat - 是否为PC端微信
         * @param {Function} resolve - Promise resolve函数
         * @param {Function} reject - Promise reject函数
         */
        function handleCodeParameter(code, isPCWeChat, resolve, reject) {
            showDebugInfo('检测到code参数，开始获取openId...');
            
            service.getWxOpenId({ code }).then((res) => {
                if (res.code === 0) {
                    let token = res.token;
                    showDebugInfo('获取openId成功，保存token');
                    store.commit("setAuthCode", token);
                    isAuthInProgress = false; // 重置标记
                    resolve(true);
                    return;
                } else if (res.code === 1) {
                    // 注册成功，待申请审核
                    if (!res.signup_token) {
                        isAuthInProgress = false;
                        Toast.fail('注册凭证获取失败，请重新授权');
                        reject(new Error('注册凭证获取失败'));
                        return;
                    }
                    let openId = res.token;
                    store.commit("setOpenId", openId);
                    sessionStorage.setItem("bid_signup_token", res.signup_token);
                    isAuthInProgress = false; // 重置标记
                    resolve(false);
                    return;
                } else {
                    showDebugInfo(`获取openId失败: ${res.msg}`);
                    
                    // PC端如果获取失败，可能需要重新授权
                    if (isPCWeChat && res.code === 40163) {
                        showDebugInfo('PC端授权过期，重新发起授权');
                        isAuthInProgress = false; // 重置标记
                        const cleanUrl = window.location.href.replace(/[?&]code=[^&]*/, '').replace(/[?&]state=[^&]*/, '');
                        window.location.replace(cleanUrl);
                        return;
                    }
                    
                    Toast.fail(res.msg);
                    isAuthInProgress = false; // 重置标记
                    reject(new Error(res.msg));
                }
            }).catch((err) => {
                showDebugInfo(`微信授权请求失败: ${err}`);
                Toast.fail('微信授权失败');
                isAuthInProgress = false; // 重置标记
                reject(err);
            });
        }
        
        // 获取当前URL中的code参数
        let code = decodeURIComponent((new RegExp('[?|&]' + 'code' + '=' + '([^&;]+?)(&|#|;|$)').exec(location.href) || [, ""])[1].replace(/\+/g, '%20')) || null;
        showDebugInfo(`获取到code参数: ${code}`);
        
        if (code) {
            handleCodeParameter(code, env.isPCWeChat, resolve, reject);
        } else {
            handleNoCodeParameter(redirectUrl, env.isPCWeChat, resolve, reject);
        }
    });
}

/**
 * 处理没有code参数的情况
 * @param {string} redirectUrl - 重定向URL
 * @param {boolean} isPCWeChat - 是否为PC端微信
 * @param {Function} resolve - Promise resolve函数
 * @param {Function} reject - Promise reject函数
 */
function handleNoCodeParameter(redirectUrl, isPCWeChat, resolve, reject) {
    try {
        showDebugInfo('未检测到code参数，准备跳转授权...');
        
        // PC端微信特殊提示（防止重复弹框）
        if (isPCWeChat && !toastShown) {
            showDebugInfo('PC端微信需要手动确认授权，请耐心等待');
            Toast({
                message: 'PC端微信需要确认授权，请点击同意',
                duration: 4000,
                forbidClick: true
            });
            toastShown = true;
            
            // 只显示一个后续提示，减少干扰
            setTimeout(() => {
                if (!toastShown) return; // 防止重复显示
                Toast({
                    message: '如果授权弹窗没有出现，请稍等或手动刷新页面',
                    duration: 4000
                });
            }, 5000);
        }
        
        // 处理重定向URL
        let finalRedirectUrl = processRedirectUrl(redirectUrl);
        showDebugInfo(`处理后的重定向URL: ${finalRedirectUrl}`);
        
        // 添加缓存破坏参数
        finalRedirectUrl = addNoCacheParam(finalRedirectUrl, isPCWeChat);
        showDebugInfo(`添加缓存参数后的URL: ${finalRedirectUrl}`);
        
        // 生成授权URL
        const authUrl = generateAuthUrl(finalRedirectUrl, isPCWeChat);
        showDebugInfo(`最终授权URL: ${authUrl}`);
        
        // 执行跳转
        executeAuthRedirect(authUrl, isPCWeChat);
        
        // 跳转后重置标记，因为页面会重新加载
        isAuthInProgress = false;
        
        // 重置弹框标记，为下次授权做准备
        toastShown = false;
        
            } catch (error) {
            showDebugInfo(`授权链接生成失败: ${error.message}`);
            Toast.fail('授权链接生成失败');
            isAuthInProgress = false; // 重置标记
            toastShown = false; // 重置弹框标记
            reject(error);
        }
}

/**
 * 处理重定向URL
 * @param {string} redirectUrl - 原始重定向URL
 * @returns {string} 处理后的URL
 */
function processRedirectUrl(redirectUrl) {
    if (redirectUrl) {
        const urlObj = new URL(redirectUrl);
        const params = urlObj.searchParams;
        
        if (params.has('redirect')) {
            const redirectPath = params.get('redirect');
            const normalizedRedirect = redirectPath.startsWith('/') ? redirectPath : '/' + redirectPath;
            params.set('redirect', normalizedRedirect);
        }
        
        return urlObj.origin + urlObj.pathname + (params.toString() ? '?' + params.toString() : '');
    } else {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('code');
        currentUrl.searchParams.delete('state');
        return currentUrl.href;
    }
}

/**
 * 生成授权URL
 * @param {string} finalRedirectUrl - 重定向URL
 * @param {boolean} isPCWeChat - 是否为PC端微信
 * @returns {string} 授权URL
 */
function generateAuthUrl(finalRedirectUrl, isPCWeChat) {
    if (isPCWeChat) {
        const pcState = `PC_${_config.wxConfig.state}_${Date.now()}`;
        // PC端微信使用更兼容的授权URL格式
        const pcAuthUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${_config.wxAppId}&redirect_uri=${encodeURIComponent(finalRedirectUrl)}&response_type=code&scope=snsapi_userinfo&state=${pcState}&connect_redirect=1`;
        
        showDebugInfo(`PC端微信授权URL: ${pcAuthUrl}`);
        return pcAuthUrl;
    } else {
        return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${_config.wxAppId}&redirect_uri=${encodeURIComponent(finalRedirectUrl)}&response_type=code&scope=${_config.wxConfig.scope}&state=${_config.wxConfig.state}&connect_redirect=1&t=${new Date().getTime()}#wechat_redirect`;
    }
}

/**
 * 执行授权重定向
 * @param {string} authUrl - 授权URL
 * @param {boolean} isPCWeChat - 是否为PC端微信
 */
function executeAuthRedirect(authUrl, isPCWeChat) {
    try {
        showDebugInfo('准备跳转到授权页面...');
        
        if (isPCWeChat) {
            showDebugInfo('PC端微信使用特殊授权流程...');
            
            // 显示用户引导（防止重复弹框）
            if (!toastShown) {
                Toast({
                    message: 'PC端微信授权即将开始，请准备确认',
                    duration: 4000,
                    forbidClick: true
                });
                toastShown = true;
            }
            
            // 延迟跳转，给用户时间看到提示信息
            setTimeout(() => {
                showDebugInfo('PC端微信开始跳转授权页面');
                showDebugInfo('如果授权弹窗没有出现，请检查：');
                showDebugInfo('1. 微信是否允许弹窗');
                showDebugInfo('2. 浏览器是否阻止了弹窗');
                showDebugInfo('3. 可以尝试手动刷新页面');
                
                // 尝试使用不同的跳转方式
                try {
                    // 先尝试replace方式
                    window.location.replace(authUrl);
                } catch (e) {
                    showDebugInfo(`replace跳转失败: ${e.message}`);
                    // 如果失败，使用href方式
                    window.location.href = authUrl;
                }
            }, 2000); // 进一步减少延迟时间
        } else {
            // 移动端使用标准流程
            window.location.replace(authUrl);
        }
        
        // 清除ServiceWorker（只在PC端微信时执行一次，防止重复清除）
        if (isPCWeChat && navigator.serviceWorker && !window.serviceWorkerCleared) {
            window.serviceWorkerCleared = true;
            navigator.serviceWorker.getRegistrations().then(function(registrations) {
                for(let registration of registrations) {
                    registration.unregister();
                }
                showDebugInfo('ServiceWorker清除完成');
            });
        }
        
        // PC端微信授权失败后的备用方案（防止重复提示）
        if (isPCWeChat && !window.backupPlanShown) {
            window.backupPlanShown = true;
            setTimeout(() => {
                showDebugInfo('PC端微信授权备用方案启动...');
                showDebugInfo('如果自动授权失败，请尝试以下操作：');
                showDebugInfo('1. 手动复制以下链接到微信中打开：');
                showDebugInfo(authUrl);
                showDebugInfo('2. 或者刷新页面重新尝试');
                showDebugInfo('3. 检查微信设置是否允许网页授权');
                
                // 显示手动操作提示（只显示一次）
                Toast({
                    message: '如果自动授权失败，请手动复制链接到微信中打开',
                    duration: 8000,
                    forbidClick: false
                });
            }, 10000); // 10秒后显示备用方案，进一步减少频率
        }
        
    } catch (e) {
        showDebugInfo(`跳转过程出错: ${e.message}`);
    }
}

/**
 * 微信支付V2版本
 * @param {object} config - 支付配置参数
 * @param {Function} callback - 支付成功回调函数
 */
export function wxPaymentV2(config, callback) {
    if (typeof WeixinJSBridge === 'undefined') {
        // 微信浏览器内置对象。参考微信官方文档
        if (document.addEventListener) {
            document.addEventListener('WeixinJSBridgeReady', onBridgeReady(config, callback), false);
        } else if (document.attachEvent) {
            document.attachEvent('WeixinJSBridgeReady', onBridgeReady(config, callback));
            document.attachEvent('onWeixinJSBridgeReady', onBridgeReady(config, callback));
        }
    } else {
        console.log('准备调用微信支付');
        onBridgeReady(config, callback);
    }
}

/**
 * 微信支付桥接就绪处理
 * @param {object} config - 支付配置参数
 * @param {Function} callback - 支付成功回调函数
 */
function onBridgeReady(config, callback) {
    WeixinJSBridge.invoke('getBrandWCPayRequest', {
        appId: config.appId,
        timeStamp: config.timeStamp,
        nonceStr: config.nonceStr,
        package: config.package,
        signType: config.signType,
        paySign: config.paySign,
    }, (res) => {
        console.log('微信支付调用结果:', res);
        
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
            console.log('支付成功');
            if (callback && typeof callback === 'function') {
                callback();
            }
        } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
            console.log('支付取消');
        } else {
            console.log('支付失败:', res.err_msg);
        }
    });
}


export function wxPaymentV3(wxConfig) {
    return new Promise((resolve, reject) => {
        readyConfig(wxConfig).then((res) => {
            wx.chooseWXPay({
                appId: wxConfig.appId,
                timestamp: wxConfig.timeStamp,
                nonceStr: wxConfig.nonceStr,
                package: wxConfig.package,
                paySign: wxConfig.paySign,
                success: function(res) {
                    console.log('支付好了');
                    resolve();
                },
                cancel: function(res) {
                    console.log('取消支付');
                },
                fail: function(res) {
                    console.log('支付失败');
                }
            })
        });
    })
}


export function wxScanCode(wxConfig) {
    return new Promise((resolve, reject) => {
        readyConfig(wxConfig, ['scanQRCode']).then(() => {
            wx.scanQRCode({
                needResult: 1,
                success: function(res) {
                    resolve(res.resultStr);
                },
                fail: err => reject(err)
            });
        })
    })
}

export function readyConfig({ appid, timestamp, nonceStr, signature }, jsApiList) {
    return new Promise((resolve, reject) => {
        console.log('weixin是什么 -> ', wx);
        let config = {
            debug: false,
            appId: appid,
            timestamp: timestamp,
            nonceStr: nonceStr,
            signature,
            jsApiList: ['checkJsApi', 'scanQRCode']
        }

        wx.config(config);

        wx.ready(() => {
            console.log('微信Config配置成功 v1')
            resolve();
        });

        wx.error((error) => {
            console.log('微信Config配置错误 v1', error);
            reject();
        })
    })
}
